import { apiRequest } from "../api";
import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { useDatadog } from "./use-datadog";
import { usePlayer } from "./use-player";

interface IModusProviderProps {
  refId?: string;
  agentName?: string;
  children: React.ReactNode;
}

interface IModusChatContext {
  _id: string;
  pl_group_id: number;
  agent: string;
  messages: { role: string; content: string }[];
}

export type SSEMessage = {
  event?: string;
  id?: string;
  data: string;
};

export const useModusProvider = (
  refId: string = null,
  agentName: string = null
) => {
  const { trackEvent, trackError } = useDatadog();
  const [agentsList, setAgentsList] = useState<
    { value: string; label: string }[]
  >([]);
  const [isWaitingForResponse, setIsWaitingForResponse] =
    useState<boolean>(null);
  const [isAudioMuted, setIsAudioMuted] = useState<boolean>(
    localStorage.getItem("isAudioMuted") === "false" ? false : true
  );

  const [activeAgent, setActiveAgent] = useState<string>(agentName);

  const [latestSpokenQuestion, setLatestSpokenQuestion] =
    useState<string>(null);

  const {
    controls: { play: playTTS, stop: stopTTS }
  } = usePlayer();

  const [modusChatContext, setModusChatContext] =
    useState<IModusChatContext>(null);

  useEffect(() => {
    localStorage.setItem("isAudioMuted", JSON.stringify(isAudioMuted));
  }, [isAudioMuted]);

  useEffect(() => {
    if (!isAudioMuted) {
      if (latestSpokenQuestion) {
        playTTS({
          itemId: "modus",
          itemType: "question",
          text: latestSpokenQuestion
        });
      }
    } else {
      stopTTS();
    }
  }, [isAudioMuted, latestSpokenQuestion]);

  useEffect(() => {
    apiRequest<{ value: string; label: string }[]>({
      subRoute: "ai",
      path: "/get_agents_list"
    }).then((data) => {
      setAgentsList(data);
    });
  }, []);

  // useEffect(() => {
  //   if (activeAgent) {
  //     fetchChatContext(agentName, null);
  //   }
  // }, [activeAgent]);

  // useEffect(() => {
  //   if (refId) {
  //     fetchChatContext(null, refId);
  //   }
  // }, [refId]);

  useEffect(() => {
    fetchChatContext(activeAgent, refId);
  }, [activeAgent, refId]);

  const fetchChatContext = async (agentName: string, topicId?: string) => {
    if (agentName || topicId) {
      setModusChatContext(null);
      const data = await apiRequest<IModusChatContext[]>({
        subRoute: "ai",
        path:
          `/get_modus_agency_histories?${agentName ? `agent=${agentName}` : ""}` +
          (topicId ? `&topic_id=${topicId}` : "")
      });

      setModusChatContext(data[0]);
      setLatestSpokenQuestion(null);
    }
  };

  const resetChatContext = async () => {
    setModusChatContext(null);
    const data = await apiRequest<IModusChatContext>({
      subRoute: "ai",
      path:
        `/create_new_agency_history?agent=${modusChatContext.agent}` +
        (refId ? `&topic_id=${refId}` : "")
    });
    setModusChatContext(data);
  };

  const latestMessage = useMemo(() => {
    if (!modusChatContext) {
      return null;
    }

    return modusChatContext.messages?.slice(-1)[0];
  }, [modusChatContext]);

  const latestVoMessage = useMemo(() => {
    if (!modusChatContext) {
      return null;
    }

    return modusChatContext.messages
      ?.filter((msg) => msg.role === "assistant")
      .slice(-1)[0];
  }, [modusChatContext]);

  const latestUserMessage = useMemo(() => {
    if (!modusChatContext) {
      return null;
    }

    return modusChatContext.messages
      ?.filter((msg) => msg.role === "user")
      .slice(-1)[0];
  }, [modusChatContext]);

  const fmtLatestMessage = useMemo(() => {
    if (!latestMessage) {
      return null;
    } else {
      if (latestMessage.role === "assistant") {
        try {
          const json = JSON.parse(
            latestMessage?.content.replace("json", "").replaceAll("```", "")
          );

          return json.final_response;
        } catch (e) {
          return latestMessage.content;
        }
      }
    }

    return latestMessage.content;
  }, [latestMessage]);

  const sendSystemMessage = async (message: string) => {
    if (!message.trim() || !modusChatContext?.pl_group_id) return;

    // Persist to backend
    await apiRequest({
      subRoute: "ai",
      path: "/add_system_message",
      method: "POST",
      body: {
        plGroupId: modusChatContext.pl_group_id,
        message
      }
    });
  };

  const sendMessage = async (
    userInput: string,
    onMessage: (msg) => void = null
  ) => {
    if (!userInput.trim()) return;

    setIsWaitingForResponse(true);

    const newMessage = { role: "user", content: userInput };
    const loadingMessage = { role: "assistant", content: "Thinking..." };

    setModusChatContext((prev) => {
      const newChatContext = { ...prev };
      newChatContext.messages = [
        ...newChatContext.messages,
        newMessage,
        loadingMessage
      ];

      return newChatContext;
    });

    trackEvent("modus_chat_sent", {
      agent: modusChatContext.agent,
      plGroupId: modusChatContext.pl_group_id,
      userInput,
      refId
    });

    try {
      const res = await apiRequest<ReadableStream>({
        subRoute: "ai",
        path: "/modus_chat_api",
        method: "POST",
        responseType: "stream",
        body: {
          userInput,
          plGroupId: modusChatContext.pl_group_id,
          agent: modusChatContext.agent,
          topicId: refId
        }
      });

      const reader = res.getReader();
      const decoder = new TextDecoder("utf-8");

      let buffer = "";

      while (true) {
        const { value, done } = await reader.read();

        if (done) break;

        buffer += decoder.decode(value, { stream: true });

        let frameEnd = 0;
        while ((frameEnd = buffer.indexOf("\n\n")) !== -1) {
          const rawFrame = buffer.slice(0, frameEnd);
          buffer = buffer.slice(frameEnd + 2);

          const msg: SSEMessage = { data: "" };

          for (const line of rawFrame.split("\n")) {
            const trimmed = line.replace(/\r$/, "");
            if (trimmed.startsWith("event:"))
              msg.event = trimmed.slice(6).trim();
            else if (trimmed.startsWith("id:"))
              msg.id = trimmed.slice(3).trim();
            else if (trimmed.startsWith("data:")) {
              // data can appear multiple times; concatenate with newline
              const chunk = trimmed.slice(5).trimStart();
              msg.data = msg.data ? `${msg.data}\n${chunk}` : chunk;
            }
          }

          if (msg.data) {
            msg.data = JSON.parse(msg.data);

            if (msg.data["message_type"] == "end_stream") {
              const finalOutput = msg.data["final_output"];

              for (let i = 0; i <= finalOutput.length; i++) {
                setTimeout(() => {
                  setModusChatContext((prev) => {
                    const newChatContext = { ...prev };
                    newChatContext.messages = newChatContext.messages
                      .slice(0, -1)
                      .concat([
                        {
                          role: "assistant",
                          content: finalOutput.substring(0, i)
                        }
                      ]);

                    return newChatContext;
                  });
                }, i * 10);
              }
            }
          }

          if (onMessage) {
            onMessage(msg);
          }

          if (msg.data["message_type"] === "generated_spoken_question") {
            setLatestSpokenQuestion(msg.data["content"]);
          }
        }
      }
    } catch (err) {
      console.log(err);
      console.log("GOT ERROR", err);
      console.error(err);
      if ((err as any)?.name === "AbortError") {
        trackEvent("connection_aborted", err);
      }
      // onError?.(err);
      trackError("error_modus_chat_error", err);
    } finally {
      setIsWaitingForResponse(false);
    }
  };

  return {
    agentsList,

    modusChatContext,
    setModusChatContext,

    resetChatContext,

    sendMessage,
    sendSystemMessage,

    isWaitingForResponse,

    isAudioMuted,
    setIsAudioMuted,

    latestMessage,
    latestVoMessage,
    latestUserMessage,

    fmtLatestMessage,

    activeAgent,
    setActiveAgent
  };
};

export const ModusProvider = ({
  children,
  refId,
  agentName
}: IModusProviderProps) => {
  const modusProvider = useModusProvider(refId, agentName);

  return (
    <ModusContext.Provider value={modusProvider}>
      {children}
    </ModusContext.Provider>
  );
};

export type ModusProps = ReturnType<typeof useModusProvider>;
export const ModusContext = createContext<ModusProps>(null);
export const useModus = () => useContext(ModusContext);
